// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import checkoutReducer, { CheckoutReducerAction } from "@flip/App/Widgets/CheckoutStepper/Checkout.reducer";
import {
    CheckoutStep,
    CHECKOUT_STEPS,
    generateSteps,
    some,
} from "@flip/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { doScroll } from "@flip/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { createContext, useReducer, useRef, useState } from "react";

export const initialCheckoutInfoState = {
    paymentProfile: "",
    poNumber: "",
    paymentMethod: "",
    cardHolderName: "",
    lastFour: "",
    expirationMonth: 1,
    expirationYear: new Date().getFullYear(),
    vatNumber: "",
    saveCard: false,
    cardNumber: "",
    cardType: "",
    possibleCardType: "",
    securityCode: "",
    useBillingAddress: true,
    address1: "",
    countryId: "",
    stateId: "",
    city: "",
    postalCode: "",
    releaseNumber: "",
    phoneNumber: "",
    rememberPhoneNumber: false,
    accountHolderName: "",
    accountNumber: "",
    routingNumber: "",
    isSavedPayment: false,
    isCreditCard: false,
    placedBy: "",
    placedByPhoneNumber: "",
    cartNotes: "",
    invalidStep: false, // This flag used to indicate if any step has invalid entries
    disablePlaceOrder: true,
    showSpinner: false
};

export type CheckoutInfoState = Partial<typeof initialCheckoutInfoState>;

export interface StepperContextData {
    steps: CheckoutStep[];
    currentStep: number;
    maxStep: number;
    checkoutInfoState: CheckoutInfoState;
    setCurrentStep: (step: number) => void;
    moveToStepHandler: (step: number) => void;
    setMaxStep: (step: number) => void;
    preventStepperChange: (hook: any, position: number) => void;
    updateCheckoutInfoState: React.Dispatch<CheckoutReducerAction>;
}

export type HasStepperContext = Pick<StepperContextData, "currentStep">;

const stepperContextData: StepperContextData = {
    steps: [],
    currentStep: 1,
    maxStep: 1,
    checkoutInfoState: {},
    setCurrentStep: (step: number) => {},
    moveToStepHandler: (step: number) => {},
    setMaxStep: (step: number) => {},
    preventStepperChange: (hook: any, position: number) => {},
    updateCheckoutInfoState: () => {},
};

export const StepperContext = createContext<StepperContextData>(stepperContextData);

const StepperContextProvider: React.FC<{ initialStep?: number }> = ({ initialStep = 1, children }) => {
    const history = useHistory();
    const { checkoutShippingPage, isPickUp, cartPage, requiresApproval, orderConrifmationCart, cartId } = useAppSelector(
        state => {
            const { cartId } = getQueryStrings(state);
            return {
                checkoutShippingPage: getPageLinkByPageType(state, "CheckoutShippingPage"),
                cartPage: getPageLinkByPageType(state, "CartPage"),
                isPickUp: getSession(state).fulfillmentMethod === FulfillmentMethod.PickUp,
                requiresApproval: canSubmitForApprovalOrder(getCurrentCartState(state).value),
                orderConrifmationCart: getCartState(state, state.pages.orderConfirmation.cartId).value,
                cartId
            }
        },
    );
    const steps = generateSteps(isPickUp, requiresApproval);
    const [currentStep, doSetCurrentStep] = useState<number>(initialStep);
    const [maxStep, doSetMaxStep] = useState<number>(initialStep);
    const [checkoutInfoState, updateCheckoutInfoState] = useReducer(checkoutReducer, initialCheckoutInfoState);
    const validationHookRef = useRef<any[]>([]);

    const setMaxStep = (state: number) => {
        doSetMaxStep(state);
    };

    // To update currentStep value to nextStep step
    const setCurrentStep = async (nextStep: number) => {
        const prevent = await some(validationHookRef.current, (item: any) => {
            const hook = item.hook.current(currentStep, nextStep);

            // check is already a promise and return
            if (typeof hook?.then === "function") {
                return hook;
            }
            return Promise.resolve(!!hook);
        });

        if (prevent) {
            return;
        }

        if (currentStep > maxStep) {
            setMaxStep(currentStep);
        }
        doSetCurrentStep(nextStep);
    };

    const preventStepperChange = (hook: any, position = 1) => {
        const vh = validationHookRef.current;
        const index = vh.findIndex((f: any) => f.position === position);
        if (index !== -1) {
            vh.splice(index, 1);
        }
        vh.push({ hook, position });
        vh.sort((a, b) => a.position - b.position);
    };

    // To move to any step
    const moveToStep = (moveToNewStep: number) => {
        if (moveToNewStep >= currentStep) {
            return;
        }

        if (moveToNewStep === CHECKOUT_STEPS.CART) {
            history.push(`${cartPage?.url}`);
        } else if (moveToNewStep === CHECKOUT_STEPS.ADDRESS) {
            history.push(`${checkoutShippingPage?.url}${cartId ? `?cartId=${cartId}` : ``}`);
        }
    
        setCurrentStep?.(moveToNewStep);
        doScroll(true);
    };

    return (
        <StepperContext.Provider
            value={{
                steps,
                currentStep,
                setCurrentStep,
                maxStep,
                checkoutInfoState,
                setMaxStep,
                preventStepperChange,
                moveToStepHandler: moveToStep,
                updateCheckoutInfoState,
            }}
        >
            {children}
        </StepperContext.Provider>
    );
};

export default StepperContextProvider;

export function withStepperContext<P extends HasStepperContext>(Component: React.ComponentType<P>) {
    return function StepperComponent(props: Omit<P, keyof HasStepperContext>) {
        return (
            <StepperContext.Consumer>
                {value => <Component {...(props as P)} currentStep={value.currentStep} />}
            </StepperContext.Consumer>
        );
    };
}
